/**
 * eEarn Deposit Dialog Content
 *
 * Deposit dialog for eEarn vaults (e.g., purrHYPE).
 * eEarn vaults use standard ERC4626 deposit() function, NOT skim().
 *
 * Deposit flows:
 * - Direct ERC20: Permit2 → deposit()
 * - Native HYPE: Wrap to WHYPE → Permit2 → deposit()
 * - Swap: wallet swap (to wallet) → Permit2 → deposit()
 */

'use client'

import { NATIVE_HYPE_GAS_RESERVE } from '@/config/consts'
import { EVC_ADDRESS, E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { invalidateBalancesCache } from '@/domain/cache'
import { useELendsList } from '@/domain/euler/elends/useELendInfo'
import { buildPermit2BatchRequest } from '@/domain/euler/evc/buildPermit2BatchRequest'
import { usePermit2Preflight } from '@/domain/euler/evc/usePermit2Preflight'
import { useWrapNativeToken } from '@/domain/euler/hooks/useWrapNativeToken'
import { WHYPE_ADDRESS_HYPEREVM, isNativeHypeAddress } from '@/domain/euler/nativeHype'
import { getPositionLabel } from '@/domain/euler/positions/usePositionName'
import { FALLBACK_SUBACCOUNT_RANGE, getSubAccountAddress } from '@/domain/euler/shared/subAccounts'
import { getFirstAvailableSubaccountId, useSubaccountStore } from '@/domain/euler/subaccounts'
import { useSubaccountControllers } from '@/domain/euler/subaccounts/useSubaccountControllers'
import { EulerSwapResponse } from '@/domain/euler/swap'
import { getSubAccount } from '@/domain/evc'
import { useWaitForTransactionReceiptUniversal } from '@/domain/hooks/useWaitForTransactionReceiptUniversal'
import { useLifiSwapQuote } from '@/domain/lifi/useLifiSwapQuote'
import { usePositionNames } from '@/domain/state'
import { useErc20RouterApproval } from '@/domain/swap/useErc20RouterApproval'
import { useSwapExecution } from '@/domain/swap/useSwapExecution'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { TimeoutErrorDisplay } from '@/features/actions/components/TimeoutErrorDisplay'
import { ActionRow } from '@/features/actions/components/action-row/ActionRow'
import type { ActionHandlerState } from '@/features/actions/logic/types'
import { isTimeoutError, useActionTimeout } from '@/features/actions/logic/useActionTimeout'
import { useTrackedLendBorrowAction } from '@/features/analytics'
import { DialogPanel } from '@/features/dialogs/common/components/DialogPanel'
import { MultiPanelDialog } from '@/features/dialogs/common/components/MultiPanelDialog'
import { TransactionOverviewDetailsItem } from '@/features/dialogs/common/components/transaction-overview/TransactionOverviewDetailsItem'
import { SourceTypeBadge } from '@/features/evault-details/components/SourceTypeBadge'
import { getVaultMarket } from '@/features/evault-details/data/products'
import { isUserRejection } from '@/lib/errors'
import { assets } from '@/ui/assets'
import { getTokenImage } from '@/ui/assets/tokens'
import { Dialog, DialogContent, DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Typography } from '@/ui/atoms/typography/Typography'
import { SupplyCapDetail } from '@/ui/molecules/supply-cap-detail/SupplyCapDetail'
import { TransactionErrorPanel } from '@/ui/molecules/transaction-error-panel/TransactionErrorPanel'
import { cn } from '@/ui/utils/style'
import { BigBlocksBanner } from '@/ui/molecules/big-blocks-banner/BigBlocksBanner'
import { normalizeDecimalInput } from '@/utils/decimalInput'
import { useDebounce } from '@/utils/useDebounce'
import { TransactionLabel, useSimulateEVCBatchCall } from '@eulerxyz/wagmi-evc'
import type {
  DepositTransactionItem,
  SwapMulticallTransactionItem,
  TransactionItems,
  WithdrawTransactionItem,
} from '@eulerxyz/wagmi-evc'
import { useQueryClient } from '@tanstack/react-query'
import { eEarnVaultImplementationAbi } from 'abis/evm/eEarnVaultImplementation'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { eulerVaultConnectorAbi } from 'abis/evm/eulerVaultConnector'
import BigNumber from 'bignumber.js'
import { ChevronDown, Loader2, Plus } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { type Address, erc20Abi, formatUnits, parseUnits } from 'viem'
import { useAccount, useBalance, useConfig, useReadContract, useReadContracts, useWriteContract } from 'wagmi'
import { EVaultDetailsData } from '../../logic/useEVaultDetails'
import { DepositSuccessView } from './components/DepositSuccessView'

interface DepositDestination {
  id: number
  label: string
  isNew: boolean
  subAccountAddress: Address
  debtSymbols: string[]
  marketName?: string
  healthFactor: number | null
  totalDebtUsd: number
}

interface EEarnDepositDialogContentProps {
  closeDialog: () => void
  evault: EVaultDetailsData
  depositAmount: string
  depositToken: Token
  isNativeDeposit: boolean
  /** Swap quote for swap + deposit flows */
  swapQuote?: EulerSwapResponse | null
  /** Where the swap input is coming from */
  swapSourceKind?: 'wallet' | 'euler' | 'pooled' | 'euler-same-underlying'
  /** Source vault for eLend sources */
  swapSourceVault?: Address
  /** Source sub-account for eLend sources (the sub-account that has the balance) */
  sourceSubAccount?: Address
  /** User-configured slippage tolerance (decimal, e.g., 0.005 for 0.5%). */
  slippage?: Percentage
  /** Vault type - eLend vaults show destination sub-account selector. Defaults to 'eEarn'. */
  vaultType?: 'eLend' | 'eEarn'
}

// Check if error is a non-blocking validation error from viem/wagmi
// These errors occur when hooks try to validate before all params are ready
function isNonBlockingValidationError(err: Error | null | undefined): boolean {
  if (!err) return false
  const message = err.message || String(err)
  // "address or code is required" - happens when contract read/simulation runs without address
  if (message.includes('address or code is required')) return true
  // "Invalid address" - similar validation error
  if (message.includes('Invalid address') && message.includes('undefined')) return true
  return false
}

// Parse error message to get a user-friendly description
function getSwapErrorDetails(err: Error | null | undefined): { title: string; reasons: string[] } | null {
  if (!err) return null
  const message = err.message || String(err)

  // Check for common swap failure patterns
  if (message.includes('execution reverted') || message.includes('revert')) {
    // Check for specific revert reasons
    if (message.includes('TRANSFER_FROM_FAILED') || message.includes('TransferFromFailed')) {
      return {
        title: 'Token transfer failed',
        reasons: ['Insufficient token approval for the gateway', 'Insufficient token balance', 'Token contract issue'],
      }
    }
    if (message.includes('INSUFFICIENT') || message.includes('insufficient')) {
      return {
        title: 'Insufficient funds',
        reasons: ['Not enough tokens in your wallet', 'Swap amount exceeds available balance'],
      }
    }
    if (message.includes('SLIPPAGE') || message.includes('slippage') || message.includes('MinOutputAmount')) {
      return {
        title: 'Price moved too much',
        reasons: [
          'Market price changed during transaction',
          'Try again with a fresh quote',
          'Consider increasing slippage tolerance',
        ],
      }
    }
    if (message.includes('EXPIRED') || message.includes('expired') || message.includes('Deadline')) {
      return {
        title: 'Quote expired',
        reasons: ['The swap quote expired before submission', 'Try again with a fresh quote'],
      }
    }
    // Generic revert
    return {
      title: 'Transaction would fail',
      reasons: [
        'The swap quote may have expired',
        'Market conditions changed',
        'Insufficient liquidity in the swap route',
        'Try again with a fresh quote or smaller amount',
      ],
    }
  }

  // Network or RPC errors
  if (message.includes('network') || message.includes('timeout') || message.includes('connection')) {
    return {
      title: 'Network error',
      reasons: ['Connection to the network failed', 'Try again in a moment'],
    }
  }

  return null
}

export function EEarnDepositDialogContent({
  closeDialog,
  evault,
  depositAmount,
  depositToken,
  swapSourceKind,
  swapSourceVault,
  sourceSubAccount,
  isNativeDeposit,
  swapQuote: originalSwapQuote,
  slippage,
  vaultType = 'eEarn',
}: EEarnDepositDialogContentProps) {
  const { address } = useAccount()
  const config = useConfig()
  const chainId = E_LEND_CHAIN_ID
  const queryClient = useQueryClient()
  const { getPositionName } = usePositionNames()

  const isELendDestination = vaultType === 'eLend'

  // Check if this is a swap flow and what kind
  const isSwapFlow = !!originalSwapQuote
  const [amountInput, setAmountInput] = useState(depositAmount)
  const { debouncedValue: debouncedAmount } = useDebounce(amountInput, amountInput, { delay: 500 })
  const effectiveDepositAmount = isSwapFlow ? depositAmount : debouncedAmount
  const isELendSource = swapSourceKind === 'euler'
  const isPooledSource = swapSourceKind === 'pooled'
  const isWalletOrPooledSource = swapSourceKind === 'wallet' || swapSourceKind === 'pooled'
  // eLend with same underlying = no swap needed, just withdraw + deposit
  const isELendSameUnderlying = swapSourceKind === 'euler-same-underlying'

  // State
  const [isSuccess, setIsSuccess] = useState(false)
  const [selectorOpen, setSelectorOpen] = useState(false)
  const [selectedDestinationId, setSelectedDestinationId] = useState<number | null>(null)
  // Store the swap output amount when swap completes to prevent it from changing
  const [fixedSwapOutput, setFixedSwapOutput] = useState<{
    amount: string
    token: { address: string; symbol: string; name: string; decimals: number }
  } | null>(null)
  // Track Euler Swap execution state (for eLend sources)
  const [eulerSwapStep, setEulerSwapStep] = useState<'idle' | 'swapping' | 'swapped'>('idle')
  // Track withdraw step for euler-same-underlying (no swap needed)
  const [withdrawStep, setWithdrawStep] = useState<'idle' | 'withdrawing' | 'withdrawn'>('idle')

  // ========== DESTINATION SUB-ACCOUNT LOGIC (eLend destinations only) ==========
  const { activeSubaccountIds } = useSubaccountStore()

  const idsToQuery = useMemo(() => {
    const fallbackIds = Array.from({ length: FALLBACK_SUBACCOUNT_RANGE }, (_, i) => i)
    return [...new Set([...fallbackIds, ...activeSubaccountIds])]
  }, [activeSubaccountIds])

  const { subaccountControllers, isLoading: isControllersLoading } = useSubaccountControllers({
    owner: address,
    subaccountIds: idsToQuery,
    enabled: !!address && isELendDestination,
  })

  const collateralContracts = useMemo(() => {
    if (!address || !isELendDestination) return []
    return idsToQuery.map((id) => ({
      address: EVC_ADDRESS as Address,
      abi: eulerVaultConnectorAbi,
      functionName: 'getCollaterals' as const,
      args: [getSubAccountAddress(address, id)] as const,
      chainId,
    }))
  }, [address, idsToQuery, chainId, isELendDestination])

  const { data: collateralData, isLoading: isCollateralsLoading } = useReadContracts({
    contracts: collateralContracts,
    query: {
      enabled: collateralContracts.length > 0,
      staleTime: 30_000,
    },
  })

  const subaccountCollaterals = useMemo(() => {
    const map = new Map<number, Address[]>()
    if (!collateralData) return map
    idsToQuery.forEach((id, index) => {
      const result = collateralData[index]
      if (result?.status === 'success') {
        map.set(id, result.result as Address[])
      } else {
        map.set(id, [])
      }
    })
    return map
  }, [collateralData, idsToQuery])

  const { vaults: elendVaults } = useELendsList(chainId)

  const destVaultMarket = useMemo(() => getVaultMarket(evault.address as Address), [evault.address])

  const vaultMetadataMap = useMemo(() => new Map(elendVaults.map((v) => [v.address.toLowerCase(), v])), [elendVaults])

  const subAccountsWithDebt = useMemo(() => {
    if (!address || !subaccountControllers) return []
    const result: { subAccountId: number; controllers: Address[]; subAccountAddress: Address }[] = []
    for (const [id, controllers] of subaccountControllers) {
      if (controllers.length === 0) continue
      const hasIncompatibleController =
        destVaultMarket &&
        controllers.some((c) => {
          const m = getVaultMarket(c)
          return m && m.key !== destVaultMarket.key
        })
      if (hasIncompatibleController) continue
      result.push({
        subAccountId: id,
        controllers,
        subAccountAddress: getSubAccountAddress(address, id),
      })
    }
    return result
  }, [address, subaccountControllers, destVaultMarket])

  const debtContracts = useMemo(() => {
    return subAccountsWithDebt.flatMap(({ controllers, subAccountAddress }) =>
      controllers.map((ctrl) => ({
        address: ctrl,
        abi: eVaultImplementationAbi,
        functionName: 'debtOf' as const,
        args: [subAccountAddress] as const,
        chainId,
      })),
    )
  }, [subAccountsWithDebt, chainId])

  const { data: debtData } = useReadContracts({
    contracts: debtContracts,
    query: {
      enabled: debtContracts.length > 0,
      staleTime: 30_000,
    },
  })

  const destinations = useMemo((): DepositDestination[] => {
    if (!address || !subaccountControllers || !isELendDestination) return []

    const dests: DepositDestination[] = []

    for (const [id, controllers] of subaccountControllers) {
      if (controllers.length === 0) {
        const collaterals = subaccountCollaterals.get(id) ?? []
        if (collaterals.length === 0) continue

        dests.push({
          id,
          label: getPositionLabel(id, getPositionName(address, id)),
          isNew: false,
          subAccountAddress: getSubAccountAddress(address, id),
          debtSymbols: [],
          marketName: undefined,
          healthFactor: null,
          totalDebtUsd: 0,
        })
        continue
      }

      const hasIncompatibleDest =
        destVaultMarket &&
        controllers.some((c) => {
          const m = getVaultMarket(c)
          return m && m.key !== destVaultMarket.key
        })
      if (hasIncompatibleDest) continue

      const controllerMarket = getVaultMarket(controllers[0]!)

      let flatOffset = 0
      for (const s of subAccountsWithDebt) {
        if (s.subAccountId === id) break
        flatOffset += s.controllers.length
      }

      const debtSymbols: string[] = []
      let totalDebtUsd = 0

      for (let i = 0; i < controllers.length; i++) {
        const ctrl = controllers[i]!
        const meta = vaultMetadataMap.get(ctrl.toLowerCase())
        const symbol = meta?.assetSymbol || 'UNKNOWN'
        const decimals = meta?.decimals ?? 18
        const price = meta?.assetPrice ?? 0

        const debtRaw = debtData?.[flatOffset + i]?.result ? (debtData[flatOffset + i]!.result as bigint) : 0n
        if (debtRaw > 0n) {
          totalDebtUsd += Number(formatUnits(debtRaw, decimals)) * price
          debtSymbols.push(symbol)
        }
      }

      dests.push({
        id,
        label: getPositionLabel(id, getPositionName(address, id)),
        isNew: false,
        subAccountAddress: getSubAccountAddress(address, id),
        debtSymbols,
        marketName: controllerMarket?.name,
        healthFactor: null,
        totalDebtUsd,
      })
    }

    const idsInUse = [...subaccountControllers.entries()]
      .filter(([id, c]) => c.length > 0 || (subaccountCollaterals.get(id)?.length ?? 0) > 0)
      .map(([id]) => id)

    const nextAvailableId = getFirstAvailableSubaccountId(idsInUse)
    if (nextAvailableId !== -1 && dests.length < FALLBACK_SUBACCOUNT_RANGE) {
      dests.push({
        id: nextAvailableId,
        label: 'New Position',
        isNew: true,
        subAccountAddress: getSubAccountAddress(address, nextAvailableId),
        debtSymbols: [],
        marketName: undefined,
        healthFactor: null,
        totalDebtUsd: 0,
      })
    }

    return dests
  }, [
    address,
    subaccountControllers,
    subaccountCollaterals,
    destVaultMarket,
    vaultMetadataMap,
    subAccountsWithDebt,
    debtData,
    isELendDestination,
    getPositionName,
  ])

  const isDestinationsLoading = isControllersLoading || isCollateralsLoading

  useEffect(() => {
    if (!isELendDestination || selectedDestinationId !== null || destinations.length === 0) return
    const subAccount0 = destinations.find((d) => d.id === 0 && !d.isNew)
    if (subAccount0) {
      setSelectedDestinationId(subAccount0.id)
    } else {
      setSelectedDestinationId(destinations[0]!.id)
    }
  }, [destinations, selectedDestinationId, isELendDestination])

  const selectedDestination = useMemo(
    () => destinations.find((d) => d.id === selectedDestinationId) ?? destinations[0] ?? null,
    [destinations, selectedDestinationId],
  )

  const handleSelectDestination = useCallback((dest: DepositDestination) => {
    setSelectedDestinationId(dest.id)
    setSelectorOpen(false)
  }, [])

  // Position: for eLend destinations, use selected sub-account; for eEarn, sub-account 0
  const position = useMemo(() => {
    if (!address) return undefined
    if (isELendDestination && selectedDestination) return selectedDestination.subAccountAddress
    return getSubAccount(address, 0)
  }, [address, isELendDestination, selectedDestination])

  // For eLend sources, get the source account from the swap quote or the passed sub-account
  // The swap quote was built with the correct sub-account that has the balance
  const sourcePosition = useMemo(() => {
    // For euler-same-underlying, use the passed source sub-account (which has the balance)
    if (isELendSameUnderlying) return sourceSubAccount || position
    // For eLend sources: prefer sourceSubAccount, then quote's accountIn, then fallback to position
    if (isELendSource) {
      return sourceSubAccount || (originalSwapQuote?.accountIn as Address) || position
    }
    return position
  }, [isELendSource, isELendSameUnderlying, originalSwapQuote?.accountIn, position, sourceSubAccount])

  // Parse amount for transactions
  const amountBigInt = useMemo(() => {
    if (isSwapFlow && originalSwapQuote) {
      try {
        return BigInt(originalSwapQuote.amountIn)
      } catch {
        return 0n
      }
    }
    try {
      return parseUnits(effectiveDepositAmount || '0', depositToken.decimals)
    } catch {
      return 0n
    }
  }, [isSwapFlow, originalSwapQuote, effectiveDepositAmount, depositToken.decimals])

  // ========== WALLET/POOLED SWAP STATE ==========
  // Track wallet swap execution state
  const [walletSwapStep, setWalletSwapStep] = useState<'idle' | 'swapping' | 'swapped'>('idle')

  // ========== EULER SWAP (for eLend sources) ==========
  // For eLend sources, we use a 2-step flow:
  // Step 1: EVC batch (withdraw from eLend + swap to wallet)
  // Step 2: Permit2 + deposit to eEarn (handled by the normal deposit flow)

  // Check if the Euler swap route is available
  const isEulerSwapRouteAvailable = useMemo(() => {
    if (!isELendSource || !originalSwapQuote) return false
    // Check if swap data exists and has the required fields
    return !!(originalSwapQuote.swap?.swapperAddress && originalSwapQuote.swap?.swapperData)
  }, [isELendSource, originalSwapQuote])

  // Build transactions for Euler swap: WITHDRAW + SWAP_MULTICALL
  const eulerSwapTransactions = useMemo((): TransactionItems => {
    if (!isEulerSwapRouteAvailable || !originalSwapQuote?.swap || !address || !sourcePosition || !swapSourceVault)
      return []

    // Use originalSwapQuote.tokenIn for the underlying token being withdrawn and swapped
    // (depositToken is the vault's share token, not the underlying)
    const tokenInAsset = {
      address: originalSwapQuote.tokenIn.address as Address,
      name: originalSwapQuote.tokenIn.name,
      symbol: originalSwapQuote.tokenIn.symbol,
      decimals: originalSwapQuote.tokenIn.decimals,
    }

    const tokenOutAsset = {
      address: originalSwapQuote.tokenOut.address as Address,
      name: originalSwapQuote.tokenOut.name,
      symbol: originalSwapQuote.tokenOut.symbol,
      decimals: originalSwapQuote.tokenOut.decimals,
    }

    const txs: TransactionItems = []

    // 1. Withdraw from eLend vault to swapper
    const withdrawToSwapper: WithdrawTransactionItem = {
      label: TransactionLabel.WITHDRAW,
      vault: swapSourceVault as Address,
      asset: tokenInAsset,
      from: sourcePosition,
      to: originalSwapQuote.swap.swapperAddress as Address,
      value: originalSwapQuote.amountIn,
      userShares: originalSwapQuote.amountIn,
      isMax: false,
    }
    txs.push(withdrawToSwapper)

    // 2. Swap multicall (outputs to user's wallet)
    const swapTx: SwapMulticallTransactionItem = {
      label: TransactionLabel.SWAP_MULTICALL,
      from: sourcePosition,
      to: address, // Output to user's wallet
      asset: tokenInAsset,
      fromVault: swapSourceVault as Address,
      toVault: address, // Wallet destination
      accountTo: sourcePosition,
      receiver: address, // User receives output in wallet
      assetIn: tokenInAsset,
      assetOut: tokenOutAsset,
      amountIn: originalSwapQuote.amountIn,
      amountOut: originalSwapQuote.amountOutMin,
      swapperAddress: originalSwapQuote.swap.swapperAddress as Address,
      swapperData: originalSwapQuote.swap.swapperData as `0x${string}`,
      calls: originalSwapQuote.swap.multicallItems?.map((item: any) => item.data) || [],
    }
    txs.push(swapTx)

    return txs
  }, [isEulerSwapRouteAvailable, originalSwapQuote, address, sourcePosition, swapSourceVault])

  // Simulate the Euler swap EVC batch
  const {
    data: eulerSwapSimulationData,
    isLoading: isEulerSwapSimulating,
    error: eulerSwapSimulationError,
  } = useSimulateEVCBatchCall({
    config,
    transactions: eulerSwapTransactions,
    query: {
      enabled: !!address && eulerSwapTransactions.length > 0,
      retry: false,
    },
  })

  // Execute Euler Swap via EVC batch
  const {
    writeContract: writeEulerSwap,
    isPending: isEulerSwapPending,
    error: eulerSwapWriteError,
    data: eulerSwapTxHash,
    reset: resetEulerSwap,
  } = useWriteContract()

  const {
    isLoading: isEulerSwapConfirming,
    isSuccess: isEulerSwapSuccess,
    error: eulerSwapConfirmError,
  } = useWaitForTransactionReceiptUniversal({
    hash: eulerSwapTxHash,
    chainId,
  })

  // Handle Euler Swap execution via EVC batch (withdraw + swap to wallet)
  // After this completes, user proceeds with Permit2 + deposit
  const handleEulerSwap = useCallback(() => {
    if (!eulerSwapSimulationData?.request) {
      console.warn('Euler swap simulation not ready')
      return
    }

    setEulerSwapStep('swapping')

    // Execute the simulated EVC batch
    writeEulerSwap(eulerSwapSimulationData.request as any)
  }, [eulerSwapSimulationData?.request, writeEulerSwap])

  // Update Euler swap step when confirmed
  useEffect(() => {
    if (isEulerSwapSuccess && eulerSwapStep === 'swapping') {
      setEulerSwapStep('swapped')
      // Capture the output amount for deposit
      if (originalSwapQuote && !fixedSwapOutput) {
        setFixedSwapOutput({
          amount: originalSwapQuote.amountOutMin,
          token: originalSwapQuote.tokenOut,
        })
      }
      // Invalidate and refetch balances after swap
      invalidateBalancesCache({
        queryClient,
        chainId,
        address,
        position: sourcePosition,
        vaultAddress: swapSourceVault,
      })
    }
  }, [
    isEulerSwapSuccess,
    eulerSwapStep,
    originalSwapQuote,
    fixedSwapOutput,
    queryClient,
    chainId,
    address,
    swapSourceVault,
    sourcePosition,
  ])

  // Reset Euler swap if error
  useEffect(() => {
    if ((eulerSwapWriteError || eulerSwapConfirmError) && eulerSwapStep === 'swapping') {
      setEulerSwapStep('idle')
      resetEulerSwap()
    }
  }, [eulerSwapWriteError, eulerSwapConfirmError, eulerSwapStep, resetEulerSwap])

  // ========== WITHDRAW (for euler-same-underlying) ==========
  // Simple withdraw from eLend to wallet - no swap needed

  // Convert asset amount to shares using previewWithdraw
  // userShares must be in share terms, not asset terms
  const { data: withdrawSharesNeeded } = useReadContract({
    address: swapSourceVault,
    abi: eVaultImplementationAbi,
    functionName: 'previewWithdraw',
    args: amountBigInt > 0n ? [amountBigInt] : undefined,
    chainId,
    query: {
      enabled: isELendSameUnderlying && !!swapSourceVault && amountBigInt > 0n,
    },
  })

  const withdrawTransactions = useMemo((): TransactionItems => {
    // Wait for withdrawSharesNeeded to load before building transactions
    // This prevents simulation from running with placeholder '0' value
    if (!isELendSameUnderlying || !address || !sourcePosition || !swapSourceVault || !withdrawSharesNeeded) return []

    const tokenAsset = {
      address: depositToken.address as Address,
      name: depositToken.name,
      symbol: String(depositToken.symbol),
      decimals: depositToken.decimals,
    }

    // Simple withdraw to user's wallet
    return [
      {
        label: TransactionLabel.WITHDRAW,
        vault: swapSourceVault,
        asset: tokenAsset,
        from: sourcePosition, // Withdraw from the sub-account
        to: address, // Send to user's wallet
        value: amountBigInt.toString(),
        userShares: withdrawSharesNeeded.toString(),
        isMax: false,
      } as TransactionItems[number],
    ]
  }, [
    isELendSameUnderlying,
    address,
    sourcePosition,
    swapSourceVault,
    depositToken,
    amountBigInt,
    withdrawSharesNeeded,
  ])

  // Simulate withdraw
  const {
    data: withdrawSimulation,
    isLoading: isWithdrawSimulating,
    error: withdrawSimulationError,
  } = useSimulateEVCBatchCall({
    config,
    transactions: withdrawTransactions,
    query: {
      enabled: isELendSameUnderlying && withdrawTransactions.length > 0 && withdrawStep === 'idle',
      retry: false,
    },
  })

  // Execute withdraw
  const {
    writeContract: writeWithdraw,
    isPending: isWithdrawPending,
    error: withdrawWriteError,
    data: withdrawTxHash,
    reset: resetWithdraw,
  } = useWriteContract()

  const {
    isLoading: isWithdrawConfirming,
    isSuccess: isWithdrawSuccess,
    error: withdrawConfirmError,
  } = useWaitForTransactionReceiptUniversal({
    hash: withdrawTxHash,
    chainId,
  })

  // Handle withdraw execution
  const handleWithdraw = useCallback(() => {
    if (!withdrawSimulation?.request) return
    setWithdrawStep('withdrawing')
    writeWithdraw(withdrawSimulation.request as any)
  }, [withdrawSimulation?.request, writeWithdraw])

  // Update withdraw step when confirmed
  useEffect(() => {
    if (isWithdrawSuccess && withdrawStep === 'withdrawing') {
      setWithdrawStep('withdrawn')
      // Set fixed output for Permit2 (same token, same amount)
      if (!fixedSwapOutput) {
        setFixedSwapOutput({
          amount: amountBigInt.toString(),
          token: {
            address: depositToken.address,
            symbol: String(depositToken.symbol),
            name: depositToken.name,
            decimals: depositToken.decimals,
          },
        })
      }
      // Invalidate balances
      invalidateBalancesCache({
        queryClient,
        chainId,
        address,
        vaultAddress: swapSourceVault,
      })
    }
  }, [
    isWithdrawSuccess,
    withdrawStep,
    fixedSwapOutput,
    amountBigInt,
    depositToken,
    queryClient,
    chainId,
    address,
    swapSourceVault,
  ])

  // Reset withdraw if error
  useEffect(() => {
    if ((withdrawWriteError || withdrawConfirmError) && withdrawStep === 'withdrawing') {
      setWithdrawStep('idle')
      resetWithdraw()
    }
  }, [withdrawWriteError, withdrawConfirmError, withdrawStep, resetWithdraw])

  // Check if depositToken is native HYPE (needed early)
  // ========== WRAP NATIVE TOKEN ==========
  // Only needed for direct native deposits (non-swap flows)
  // For swap flows with native HYPE, the swap router handles native via msg.value
  const wrapHook = useWrapNativeToken({
    amount: amountBigInt,
    chainId,
    enabled: isNativeDeposit && !isSwapFlow,
  })

  // ========== WALLET/POOLED SWAP FLOW ==========
  // For wallet/pooled sources: swap to wallet, then Permit2 deposit
  // Check if depositToken is native HYPE for wallet swaps
  const isWalletNativeHypeSwap = isWalletOrPooledSource && isNativeHypeAddress(depositToken.address as Address)

  // Get LiFi swap quote for wallet sources
  const walletSwapQuote = useLifiSwapQuote({
    tokenIn: depositToken.address as Address,
    tokenOut: evault.underlyingAsset as Address,
    amount: amountBigInt > 0n ? amountBigInt.toString() : undefined,
    userAddress: address,
    slippage: slippage ? Math.round(Number(slippage) * 10000) : 50, // Convert from decimal (0.005) to BPS (50)
    enabled: isWalletOrPooledSource && !isELendSameUnderlying && walletSwapStep !== 'swapped',
    tokenInInfo: {
      symbol: depositToken.symbol,
      name: depositToken.name,
      decimals: depositToken.decimals,
    },
    tokenOutInfo: {
      symbol: evault.underlyingTokenInfo?.symbol || evault.assetSymbol || 'TOKEN',
      name: evault.underlyingTokenInfo?.name || evault.assetSymbol || 'Token',
      decimals: evault.underlyingTokenInfo?.decimals ?? 18,
    },
  })

  // Router approval for wallet swap (ERC20 only)
  const routerApproval = useErc20RouterApproval({
    tokenAddress: depositToken.address as Address,
    routerAddress: walletSwapQuote.quote?.approvalAddress,
    amount: amountBigInt,
    chainId,
    enabled: isWalletOrPooledSource && !isWalletNativeHypeSwap && walletSwapStep !== 'swapped',
  })

  // Execute wallet swap
  // For native HYPE swaps, LiFi includes the native value in txValue
  const walletSwapExecution = useSwapExecution({
    routerAddress: walletSwapQuote.quote?.routerAddress,
    calldata: walletSwapQuote.quote?.calldata,
    outputToken: evault.underlyingAsset as Address,
    chainId,
    enabled: isWalletOrPooledSource && walletSwapStep !== 'swapped',
    value:
      walletSwapQuote.quote?.txValue && walletSwapQuote.quote.txValue !== '0'
        ? BigInt(walletSwapQuote.quote.txValue)
        : undefined,
  })

  // Track wallet swap completion - use actual output from transaction logs
  useEffect(() => {
    if (walletSwapExecution.isSuccess && walletSwapStep === 'swapping') {
      // Use actual swap output from transaction logs (more accurate than quote estimate)
      if (walletSwapExecution.output) {
        setFixedSwapOutput({
          amount: walletSwapExecution.output.amount,
          token: {
            address: walletSwapExecution.output.tokenAddress,
            symbol: walletSwapQuote.quote?.tokenOut.symbol || 'TOKEN',
            name: walletSwapQuote.quote?.tokenOut.name || 'Token',
            decimals: walletSwapQuote.quote?.tokenOut.decimals || 18,
          },
        })
        setWalletSwapStep('swapped')
      } else if (walletSwapQuote.quote) {
        // Fallback to quote minOutputAmount if logs parsing failed
        setFixedSwapOutput({
          amount: walletSwapQuote.quote.minOutputAmount,
          token: {
            address: walletSwapQuote.quote.tokenOut.address,
            symbol: walletSwapQuote.quote.tokenOut.symbol,
            name: walletSwapQuote.quote.tokenOut.name,
            decimals: walletSwapQuote.quote.tokenOut.decimals,
          },
        })
        setWalletSwapStep('swapped')
      } else {
        // Edge case: swap succeeded but we can't determine output - reset to allow retry
        console.error('Swap succeeded but could not determine output amount')
        setWalletSwapStep('idle')
      }
    }
  }, [walletSwapExecution.isSuccess, walletSwapExecution.output, walletSwapStep, walletSwapQuote.quote])

  // Handle wallet swap execution
  const handleWalletSwap = useCallback(() => {
    if (!address || !walletSwapQuote.quote) return
    setWalletSwapStep('swapping')
    walletSwapExecution.executeSwap()
  }, [address, walletSwapQuote.quote, walletSwapExecution])

  // Reset wallet swap on error
  useEffect(() => {
    if (walletSwapExecution.error && walletSwapStep === 'swapping') {
      setWalletSwapStep('idle')
    }
  }, [walletSwapExecution.error, walletSwapStep])

  // ========== DISPLAY VALUES ==========
  // Gateway swap flows disabled - only use originalSwapQuote for eLend sources
  const displayAmount = useMemo(() => {
    // For eLend sources, use original quote
    if (isSwapFlow && isELendSource && originalSwapQuote) {
      return formatUnits(BigInt(originalSwapQuote.amountIn), originalSwapQuote.tokenIn.decimals)
    }
    // Wallet/pooled swap flows disabled - gateway was exploited
    return effectiveDepositAmount
  }, [isSwapFlow, isELendSource, originalSwapQuote, effectiveDepositAmount])

  const displayToken = useMemo(() => {
    // For eLend sources, use original quote
    if (isSwapFlow && isELendSource && originalSwapQuote) {
      return {
        symbol: originalSwapQuote.tokenIn.symbol,
        address: originalSwapQuote.tokenIn.address,
        decimals: originalSwapQuote.tokenIn.decimals,
        logoURI: originalSwapQuote.tokenIn.logoURI,
      }
    }
    // For pooled sources, tokenIn is an aToken (e.g. hyUSDC). Keep symbol for labels,
    // but prefer underlying token image fallback (USDC) for icon rendering.
    if (isPooledSource) {
      const pooledSymbol = String(depositToken.symbol)
      const underlyingSymbol =
        pooledSymbol.startsWith('hy') && pooledSymbol.length > 2 ? pooledSymbol.slice(2) : pooledSymbol
      return {
        symbol: pooledSymbol,
        address: depositToken.address,
        decimals: depositToken.decimals,
        logoURI: depositToken.logoURI || getTokenImage(TokenSymbol(underlyingSymbol)),
      }
    }
    // Wallet/pooled swap flows disabled - gateway was exploited
    return depositToken
  }, [isSwapFlow, isELendSource, isPooledSource, originalSwapQuote, depositToken])

  // ========== BALANCE CHECKS ==========
  // Check if depositToken is native HYPE (for swap flows where isNativeDeposit is false)
  const isNativeHypeToken = isNativeHypeAddress(depositToken.address as Address)

  // For eLend same underlying: fetch source vault balance
  const { data: sourceVaultShares } = useReadContract({
    address: swapSourceVault as Address,
    abi: eVaultImplementationAbi,
    functionName: 'balanceOf',
    args: sourcePosition ? [sourcePosition] : undefined,
    chainId,
    query: {
      enabled: isELendSameUnderlying && !!swapSourceVault && !!sourcePosition,
      refetchInterval: 10_000,
    },
  })

  const { data: sourceVaultBalance } = useReadContract({
    address: swapSourceVault as Address,
    abi: eVaultImplementationAbi,
    functionName: 'convertToAssets',
    args: sourceVaultShares !== undefined ? [sourceVaultShares] : undefined,
    chainId,
    query: {
      enabled: isELendSameUnderlying && !!swapSourceVault && sourceVaultShares !== undefined,
      refetchInterval: 10_000,
    },
  })

  const { data: walletBalanceRaw } = useReadContract({
    address: depositToken.address as Address,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    chainId,
    query: {
      // Don't query ERC20 balance for native deposits or native HYPE tokens
      enabled: !!address && !isNativeDeposit && !isNativeHypeToken,
      refetchInterval: 10_000,
    },
  })

  const { data: whypeBalanceRaw, refetch: refetchWhypeBalance } = useReadContract({
    address: WHYPE_ADDRESS_HYPEREVM,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    chainId,
    query: {
      // Always enable for native deposits so balance is ready after wrap
      enabled: !!address && isNativeDeposit,
      refetchInterval: 5_000,
      // Don't use stale cached data
      staleTime: 0,
    },
  })

  // Refetch WHYPE balance when wrap completes
  useEffect(() => {
    if (isNativeDeposit && wrapHook.step === 'wrapped') {
      // Small delay to allow blockchain state to settle
      const timer = setTimeout(() => {
        void refetchWhypeBalance()
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [isNativeDeposit, wrapHook.step, refetchWhypeBalance])

  const { data: nativeBalance } = useBalance({
    address,
    chainId,
    query: {
      // Enable for native deposits OR native HYPE token in swap flows
      enabled: !!address && (isNativeDeposit || isNativeHypeToken),
      refetchInterval: 10_000,
    },
  })

  const activeWalletBalanceRaw = useMemo(() => {
    // For native HYPE (either direct deposit or swap flow), use native balance
    if (isNativeDeposit || isNativeHypeToken) {
      if (wrapHook.step === 'wrapped') return whypeBalanceRaw
      return nativeBalance?.value
    }
    return walletBalanceRaw
  }, [isNativeDeposit, isNativeHypeToken, wrapHook.step, whypeBalanceRaw, nativeBalance?.value, walletBalanceRaw])

  const activeSymbol = isNativeDeposit ? (wrapHook.step === 'wrapped' ? 'WHYPE' : 'HYPE') : depositToken.symbol

  // Shared calculation for available balance after gas reserve (in base units as BigNumber)
  // For native HYPE from wallet (not eLend), subtract gas reserve to ensure user has funds for tx fees
  const availableBalanceRaw = useMemo(() => {
    if (activeWalletBalanceRaw === undefined) return undefined

    const rawBalanceBN = new BigNumber(activeWalletBalanceRaw.toString())

    if ((isNativeDeposit || isNativeHypeToken) && !isELendSource) {
      const gasReserveInBaseUnits = new BigNumber(NATIVE_HYPE_GAS_RESERVE)
        .shiftedBy(depositToken.decimals)
        .integerValue()
      const available = rawBalanceBN.minus(gasReserveInBaseUnits)
      return available.isNegative() ? new BigNumber(0) : available
    }

    return rawBalanceBN
  }, [activeWalletBalanceRaw, depositToken.decimals, isNativeDeposit, isNativeHypeToken, isELendSource])

  // Calculate wallet balance for display (normalized units)
  const walletBalance = useMemo(() => {
    if (!availableBalanceRaw) return 0
    return availableBalanceRaw.shiftedBy(-depositToken.decimals).toNumber()
  }, [availableBalanceRaw, depositToken.decimals])

  const maxSpendableBalanceRaw =
    isNativeDeposit && !isELendSource
      ? availableBalanceRaw
        ? BigInt(availableBalanceRaw.toFixed(0))
        : undefined
      : activeWalletBalanceRaw

  // Check if deposit amount exceeds available balance
  // For native deposits: after wrap completes, the balance check is no longer relevant
  // because we've already wrapped the exact amount of HYPE to WHYPE
  const insufficientBalance = useMemo(() => {
    // After wrap completes, skip balance check - we wrapped the exact amount
    if (isNativeDeposit && !isSwapFlow && wrapHook.step === 'wrapped') return false
    // After wallet swap completes, skip balance check - we already have the swapped tokens
    if (isWalletOrPooledSource && walletSwapStep === 'swapped') return false
    if (!availableBalanceRaw) return false
    return new BigNumber(amountBigInt.toString()).gt(availableBalanceRaw)
  }, [
    availableBalanceRaw,
    amountBigInt,
    isNativeDeposit,
    isSwapFlow,
    wrapHook.step,
    isWalletOrPooledSource,
    walletSwapStep,
  ])

  // For eLend/pooled sources: skip the insufficientBalance check
  // because the balance is in the source vault/protocol, not the wallet
  const canDeposit = useMemo(() => {
    // After Euler swap completes, we have the output tokens - skip input balance check
    if (isELendSource && eulerSwapStep === 'swapped' && fixedSwapOutput) {
      return !!address && !!position && BigInt(fixedSwapOutput.amount) > 0n
    }
    // For pooled sources: skip wallet balance check entirely since balance is in aTokens
    // The swap will convert aTokens to output tokens
    // Allow even while quote is loading - we'll validate when quote arrives
    if (isPooledSource) {
      return !!address && !!position && amountBigInt > 0n
    }
    // For eLend sources: skip wallet balance check (balance is in vault)
    if (isELendSource) {
      return !!address && !!position && amountBigInt > 0n
    }
    // For eLend same underlying: skip wallet balance check (balance is in source vault)
    if (isELendSameUnderlying) {
      return !!address && !!position && amountBigInt > 0n
    }
    // For wallet/pooled swaps after completion: skip input balance check
    if (isWalletOrPooledSource && walletSwapStep === 'swapped' && fixedSwapOutput) {
      return !!address && !!position && BigInt(fixedSwapOutput.amount) > 0n
    }
    // For wallet/pooled swaps after completion but missing output: can't proceed
    if (isWalletOrPooledSource && walletSwapStep === 'swapped' && !fixedSwapOutput) {
      return false
    }
    // For wallet/pooled swaps before completion: need a valid swap quote
    if (isWalletOrPooledSource) {
      return !!address && amountBigInt > 0n && !insufficientBalance && !!position && !!walletSwapQuote.quote
    }
    // For native deposits after wrap: skip input balance check - we wrapped the exact amount
    if (isNativeDeposit && !isSwapFlow && wrapHook.step === 'wrapped') {
      return !!address && !!position && amountBigInt > 0n
    }
    // Default check: ensure input amount doesn't exceed wallet balance
    return !!address && amountBigInt > 0n && !insufficientBalance && !!position
  }, [
    isELendSource,
    isELendSameUnderlying,
    isWalletOrPooledSource,
    isNativeDeposit,
    isSwapFlow,
    eulerSwapStep,
    walletSwapStep,
    wrapHook.step,
    fixedSwapOutput,
    isPooledSource,
    address,
    position,
    amountBigInt,
    insufficientBalance,
    walletSwapQuote.quote,
  ])

  // ========== EXPECTED SHARES ==========
  const depositAmountBigInt = useMemo(() => {
    if (isSwapFlow) {
      if (fixedSwapOutput) {
        try {
          return BigInt(fixedSwapOutput.amount)
        } catch {
          return 0n
        }
      }
      // Gateway swap flows disabled - swapQuote is always null
      return 0n
    }
    return amountBigInt
  }, [isSwapFlow, fixedSwapOutput, amountBigInt])

  const { data: expectedSharesRaw } = useReadContract({
    address: evault.address,
    abi: eEarnVaultImplementationAbi,
    functionName: 'previewDeposit',
    args: depositAmountBigInt > 0n ? [depositAmountBigInt] : undefined,
    chainId,
    query: {
      enabled: depositAmountBigInt > 0n,
    },
  })

  const calculatedExpectedShares = useMemo(() => {
    if (expectedSharesRaw !== undefined) {
      return formatUnits(expectedSharesRaw, evault.decimals)
    }
    return undefined
  }, [expectedSharesRaw, evault.decimals])

  // ========== PERMIT2 + DEPOSIT ==========
  const permit2Token = useMemo(() => {
    // For eLend same underlying: after withdraw, use deposit token
    if (isELendSameUnderlying && withdrawStep === 'withdrawn') {
      return depositToken
    }
    // For eLend sources: use the output token from Euler Swap
    if (isELendSource && eulerSwapStep === 'swapped' && fixedSwapOutput) {
      return new Token({
        address: CheckedAddress(fixedSwapOutput.token.address),
        symbol: TokenSymbol(fixedSwapOutput.token.symbol),
        name: fixedSwapOutput.token.name,
        decimals: fixedSwapOutput.token.decimals,
        unitPriceUsd: '0',
      })
    }
    // For wallet/pooled sources: use the output token from the wallet swap
    if (isWalletOrPooledSource && walletSwapStep === 'swapped' && fixedSwapOutput) {
      return new Token({
        address: CheckedAddress(fixedSwapOutput.token.address),
        symbol: TokenSymbol(fixedSwapOutput.token.symbol),
        name: fixedSwapOutput.token.name,
        decimals: fixedSwapOutput.token.decimals,
        unitPriceUsd: '0',
      })
    }
    if (isNativeDeposit) {
      if (wrapHook.step === 'wrapped') {
        return new Token({
          address: CheckedAddress(evault.underlyingAsset),
          symbol: TokenSymbol('WHYPE'),
          name: 'Wrapped HYPE',
          decimals: depositToken.decimals,
          unitPriceUsd: '0',
        })
      }
      return undefined
    }
    // Safety: never return a native token for permit2 - native tokens can't have allowance checked
    if (isNativeHypeAddress(depositToken.address as Address)) {
      return undefined
    }
    return depositToken
  }, [
    isELendSameUnderlying,
    withdrawStep,
    isELendSource,
    eulerSwapStep,
    isWalletOrPooledSource,
    walletSwapStep,
    fixedSwapOutput,
    isNativeDeposit,
    wrapHook.step,
    evault.underlyingAsset,
    depositToken,
  ])

  const permit2Amount = useMemo(() => {
    // For eLend same underlying: use the deposit amount (same token)
    if (isELendSameUnderlying) {
      return amountBigInt
    }
    // For both eLend and wallet/pooled swap flows, use the fixed swap output after swap completes
    if ((isSwapFlow || isELendSource) && fixedSwapOutput) {
      try {
        return BigInt(fixedSwapOutput.amount)
      } catch {
        return 0n
      }
    }
    return amountBigInt
  }, [isELendSameUnderlying, isSwapFlow, isELendSource, fixedSwapOutput, amountBigInt])

  const priorStepComplete = useMemo(() => {
    // For eLend same underlying: prior step is the withdraw
    if (isELendSameUnderlying) return withdrawStep === 'withdrawn'
    // For eLend sources: prior step is the Euler Swap
    if (isELendSource) return eulerSwapStep === 'swapped'
    // For wallet/pooled swap flows: prior step is wallet swap completion
    if (isWalletOrPooledSource && isSwapFlow) return walletSwapStep === 'swapped'
    // For native deposits: prior step is wrap completion
    if (isNativeDeposit && !isSwapFlow) return wrapHook.step === 'wrapped'
    return true
  }, [
    isELendSameUnderlying,
    withdrawStep,
    isELendSource,
    eulerSwapStep,
    isSwapFlow,
    isNativeDeposit,
    wrapHook.step,
    isWalletOrPooledSource,
    walletSwapStep,
  ])

  const permit2 = usePermit2Preflight({
    token: permit2Token,
    spender: evault.address as Address,
    amount: permit2Amount,
    // Require permit2Token to be defined (safety guard against native tokens)
    // Native HYPE: enabled AFTER wrapping is complete
    // eLend same underlying: enabled AFTER withdraw completes
    // Wallet/pooled swap: enabled AFTER swap completes
    enabled: priorStepComplete && canDeposit && !!address && !!permit2Token,
  })

  // ========== GATEWAY DEPOSIT REMOVED ==========
  // Gateway was exploited - all flows now use Permit2
  // These are placeholder values for error display compatibility
  const gatewayDepositSimulationError = null as Error | null
  const gatewayDepositWriteError = null as Error | null
  const gatewayDepositConfirmError = null as Error | null

  // ========== DEPOSIT TRANSACTION (EVK - via Permit2) ==========
  const transactions = useMemo((): TransactionItems => {
    if (!address || !position) return []
    // For eLend same underlying: wait for withdraw to complete
    if (isELendSameUnderlying && withdrawStep !== 'withdrawn') return []
    // For eLend sources (with swap): wait for Euler Swap to complete
    if (isELendSource && eulerSwapStep !== 'swapped') return []
    // For wallet/pooled sources: wait for wallet swap to complete
    if (isWalletOrPooledSource && walletSwapStep !== 'swapped') return []
    // For native deposits: wait for wrap to complete
    if (isNativeDeposit && !isSwapFlow && wrapHook.step !== 'wrapped') return []

    // After swap completes, use the fixedSwapOutput token info
    const tokenInfo = (isELendSource || isWalletOrPooledSource) && fixedSwapOutput ? fixedSwapOutput.token : null
    const asset = {
      address: (tokenInfo
        ? tokenInfo.address
        : isNativeDeposit
          ? evault.underlyingAsset // WHYPE address
          : depositToken.address) as Address,
      name: tokenInfo ? tokenInfo.name : isNativeDeposit ? 'WHYPE' : depositToken.name,
      symbol: tokenInfo ? tokenInfo.symbol : isNativeDeposit ? 'WHYPE' : depositToken.symbol,
      decimals: tokenInfo ? tokenInfo.decimals : depositToken.decimals,
    }

    const depositValue =
      (isELendSource || isWalletOrPooledSource) && fixedSwapOutput ? fixedSwapOutput.amount : amountBigInt.toString()
    if (depositValue === '0') return []

    const depositTx: DepositTransactionItem = {
      label: TransactionLabel.DEPOSIT,
      from: address,
      to: position,
      asset,
      vault: evault.address as Address,
      value: depositValue,
    }

    return [depositTx]
  }, [
    address,
    position,
    depositToken,
    evault.address,
    evault.underlyingAsset,
    amountBigInt,
    isNativeDeposit,
    isSwapFlow,
    isELendSource,
    isELendSameUnderlying,
    isWalletOrPooledSource,
    eulerSwapStep,
    walletSwapStep,
    withdrawStep,
    fixedSwapOutput,
    wrapHook.step,
  ])

  const simulationEnabled = useMemo(() => {
    // For eLend same underlying: wait for withdraw to complete
    if (isELendSameUnderlying) return withdrawStep === 'withdrawn' && canDeposit
    // For eLend sources (with swap): wait for Euler Swap to complete
    if (isELendSource) return eulerSwapStep === 'swapped' && canDeposit
    // For wallet/pooled swap flows: wait for wallet swap to complete
    if (isWalletOrPooledSource) return walletSwapStep === 'swapped' && canDeposit
    // For native deposits: wait for wrap to complete
    if (isNativeDeposit && !isSwapFlow) return wrapHook.step === 'wrapped' && canDeposit
    return canDeposit
  }, [
    isELendSameUnderlying,
    withdrawStep,
    isELendSource,
    eulerSwapStep,
    canDeposit,
    isNativeDeposit,
    wrapHook.step,
    isWalletOrPooledSource,
    walletSwapStep,
    isSwapFlow,
  ])

  const {
    data: simulationData,
    isLoading: isSimulating,
    error: simulationError,
  } = useSimulateEVCBatchCall({
    config,
    transactions,
    query: {
      enabled: transactions.length > 0 && simulationEnabled,
      retry: false,
    },
  })

  const finalRequest = simulationData?.request

  const { writeContract, isPending: isWriting, error: writeError, data: txHash } = useWriteContract()

  const {
    isLoading: isWaiting,
    isSuccess: isConfirmed,
    error: waitError,
  } = useWaitForTransactionReceiptUniversal({
    hash: txHash,
  })
  const { markSubmitted } = useTrackedLendBorrowAction({
    actionType: 'deposit',
    token: depositToken.symbol,
    market: evault.address,
    marketType: 'vault',
    amount: effectiveDepositAmount,
    txHash,
    isConfirmed,
    error: writeError || waitError || simulationError || permit2.error,
  })

  const handleExecute = useCallback(async () => {
    if (!address || !position || !finalRequest) return

    markSubmitted()

    try {
      if (simulationData) {
        const executionRequest = buildPermit2BatchRequest({
          simulationData: { ...simulationData, request: finalRequest as Record<string, unknown> },
          permitTx: permit2.status === 'ready' ? permit2.permitTx : null,
          chainId,
        })
        writeContract(executionRequest as any)
      } else {
        writeContract(finalRequest as any)
      }
    } catch {
      // Silently fail - errors are handled by wagmi hooks
    }
  }, [
    address,
    chainId,
    finalRequest,
    markSubmitted,
    permit2.permitTx,
    permit2.status,
    position,
    simulationData,
    writeContract,
  ])

  // ========== ACTION TIMEOUT TRACKING ==========
  // Track timeout for deposit execution to provide feedback when operations hang
  const depositTimeout = useActionTimeout({
    timeoutMs: 15000, // 15 seconds for deposit operations
    isLoading: isWriting || isWaiting || isSimulating,
    isSuccess: isConfirmed,
    error: writeError || waitError || simulationError || permit2.error,
  })

  // Wrapped execute handler that tracks timeout
  const handleExecuteWithTimeout = useCallback(() => {
    depositTimeout.handleActionClick(handleExecute)
  }, [depositTimeout, handleExecute])

  // ========== STATUS TRACKING ==========
  // Wrap status (for native HYPE deposits)
  const wrapStatus: ActionHandlerState['status'] = useMemo(() => {
    if (!isNativeDeposit || isSwapFlow) return 'success'
    if (wrapHook.step === 'wrapped') return 'success'
    if (wrapHook.isLoading) return 'loading'
    if (wrapHook.error) return 'ready' // Allow retry on error
    if (!canDeposit) return 'disabled'
    return 'ready'
  }, [isNativeDeposit, isSwapFlow, wrapHook.step, wrapHook.isLoading, wrapHook.error, canDeposit])

  // Withdraw status (for eLend same underlying)
  const withdrawStatus: ActionHandlerState['status'] = useMemo(() => {
    if (!isELendSameUnderlying) return 'success'
    if (withdrawStep === 'withdrawn') return 'success'
    if (isWithdrawPending || isWithdrawConfirming) return 'loading'
    if (isWithdrawSimulating) return 'loading'
    if (withdrawSimulationError) {
      return 'disabled'
    }
    if (!withdrawSimulation?.request) {
      if (withdrawTransactions.length === 0) return 'disabled'
      return 'loading'
    }
    return 'ready'
  }, [
    isELendSameUnderlying,
    withdrawStep,
    isWithdrawPending,
    isWithdrawConfirming,
    isWithdrawSimulating,
    withdrawSimulationError,
    withdrawSimulation?.request,
    withdrawTransactions.length,
  ])

  // Euler Swap status (for eLend sources) - EVC batch: withdraw + swap to wallet
  // This is Step 1 of 2: swap tokens to wallet, then Permit2 + deposit
  const eulerSwapStatus: ActionHandlerState['status'] = useMemo(() => {
    if (!isELendSource) return 'success'
    if (eulerSwapStep === 'swapped') return 'success'
    if (!isEulerSwapRouteAvailable) return 'disabled' // No swap route available
    if (isEulerSwapPending || isEulerSwapConfirming) return 'loading'
    // Wait for simulation to be ready
    if (isEulerSwapSimulating) return 'loading'
    if (!eulerSwapSimulationData?.request) return 'disabled'
    if (!originalSwapQuote?.swap || !address || !sourcePosition || !swapSourceVault) return 'disabled'
    return 'ready'
  }, [
    isELendSource,
    eulerSwapStep,
    isEulerSwapRouteAvailable,
    isEulerSwapPending,
    isEulerSwapConfirming,
    isEulerSwapSimulating,
    eulerSwapSimulationData?.request,
    originalSwapQuote?.swap,
    address,
    sourcePosition,
    swapSourceVault,
  ])

  // Swap approval status (for wallet/pooled sources - ERC20 only)
  // Native HYPE swaps don't need approval - they use msg.value, not transferFrom
  const swapApproveStatus: ActionHandlerState['status'] = useMemo(() => {
    if (!isWalletOrPooledSource) return 'success'
    // Native HYPE swaps don't need approval - they use msg.value
    if (isWalletNativeHypeSwap) return 'success'
    // Swap already completed - no need to show approval
    if (walletSwapStep === 'swapped') return 'success'
    if (routerApproval.step === 'approved') return 'success'
    if (!routerApproval.needsApproval) return 'success'
    if (routerApproval.isLoading) return 'loading'
    if (!routerApproval.isAllowanceChecked) return 'loading'
    return 'ready'
  }, [
    isWalletOrPooledSource,
    isWalletNativeHypeSwap,
    walletSwapStep,
    routerApproval.step,
    routerApproval.needsApproval,
    routerApproval.isLoading,
    routerApproval.isAllowanceChecked,
  ])

  // Wallet swap status (for wallet/pooled sources)
  const walletSwapStatus: ActionHandlerState['status'] = useMemo(() => {
    if (!isWalletOrPooledSource) return 'success'
    if (walletSwapStep === 'swapped') return 'success'
    if (walletSwapExecution.isLoading || walletSwapStep === 'swapping') return 'loading'
    if (walletSwapQuote.isLoading || !walletSwapQuote.quote) return 'disabled'
    // For ERC20 swaps: check approval status
    // Native HYPE swaps don't need approval - they use msg.value
    if (!isWalletNativeHypeSwap) {
      if (!routerApproval.isAllowanceChecked) return 'disabled'
      if (routerApproval.needsApproval && routerApproval.step !== 'approved') return 'disabled'
    }
    if (!canDeposit) return 'disabled'
    if (walletSwapExecution.error && !isUserRejection(walletSwapExecution.error)) return 'disabled'
    return 'ready'
  }, [
    isWalletOrPooledSource,
    walletSwapStep,
    walletSwapExecution.isLoading,
    walletSwapExecution.error,
    walletSwapQuote.isLoading,
    walletSwapQuote.quote,
    isWalletNativeHypeSwap,
    routerApproval.isAllowanceChecked,
    routerApproval.needsApproval,
    routerApproval.step,
    canDeposit,
  ])

  // Always show router-approval step for ERC20 wallet/pooled swap flows.
  // If allowance is already sufficient, swapApproveStatus marks it as completed.
  const showSwapRouterApprovalStep = isWalletOrPooledSource && !isWalletNativeHypeSwap

  const walletSwapStepOffset = showSwapRouterApprovalStep ? 1 : 0

  const approveStatus: ActionHandlerState['status'] = useMemo(() => {
    // For eLend same underlying: need Permit2 AFTER withdraw completes
    if (isELendSameUnderlying && withdrawStep !== 'withdrawn') return 'disabled'
    // For eLend sources: need Permit2 AFTER swap completes (Step 2)
    if (isELendSource && eulerSwapStep !== 'swapped') return 'disabled'
    // For wallet/pooled sources: need Permit2 AFTER wallet swap completes
    if (isWalletOrPooledSource && walletSwapStep !== 'swapped') return 'disabled'
    // For native deposits: need Permit2 AFTER wrap completes
    if (isNativeDeposit && !isSwapFlow && wrapHook.step !== 'wrapped') return 'disabled'
    if (!priorStepComplete) return 'disabled'
    // If prior step is complete but permit2 is idle, it's loading allowance data
    // Only show disabled if canDeposit is false (e.g., amount is 0)
    if (permit2.status === 'idle') return canDeposit ? 'loading' : 'disabled'
    if (permit2.status === 'needs_approval') return 'ready'
    if (permit2.status === 'approving' || permit2.isApproving) return 'loading'
    return 'success'
  }, [
    permit2.status,
    permit2.isApproving,
    priorStepComplete,
    canDeposit,
    isNativeDeposit,
    isSwapFlow,
    isELendSource,
    isELendSameUnderlying,
    isWalletOrPooledSource,
    eulerSwapStep,
    walletSwapStep,
    withdrawStep,
    wrapHook.step,
  ])

  const signStatus: ActionHandlerState['status'] = useMemo(() => {
    // For eLend same underlying: need Permit2 AFTER withdraw completes
    if (isELendSameUnderlying && withdrawStep !== 'withdrawn') return 'disabled'
    // For eLend sources: need Permit2 AFTER swap completes (Step 3)
    if (isELendSource && eulerSwapStep !== 'swapped') return 'disabled'
    // For wallet/pooled sources: need Permit2 AFTER wallet swap completes
    if (isWalletOrPooledSource && walletSwapStep !== 'swapped') return 'disabled'
    // For native deposits: need Permit2 AFTER wrap completes
    if (isNativeDeposit && !isSwapFlow && wrapHook.step !== 'wrapped') return 'disabled'
    if (!priorStepComplete) return 'disabled'
    // If prior step is complete but permit2 is idle, it's loading allowance data
    if (permit2.status === 'idle') return canDeposit ? 'loading' : 'disabled'
    if (permit2.status === 'needs_approval' || permit2.status === 'approving' || permit2.isApproving) return 'disabled'
    if (permit2.status === 'needs_signature') return 'ready'
    if (permit2.status === 'signing' || permit2.isSigning) return 'loading'
    return 'success'
  }, [
    permit2.status,
    permit2.isApproving,
    permit2.isSigning,
    priorStepComplete,
    canDeposit,
    isNativeDeposit,
    isSwapFlow,
    isELendSource,
    isELendSameUnderlying,
    isWalletOrPooledSource,
    eulerSwapStep,
    walletSwapStep,
    withdrawStep,
    wrapHook.step,
  ])

  const executeStatus: ActionHandlerState['status'] = useMemo(() => {
    // Active transaction states take priority - never allow retry while tx is pending
    if (isWriting || isWaiting) return 'loading'

    // Check prerequisites - must be met even after timeout
    // For eLend same underlying: need deposit AFTER withdraw completes
    if (isELendSameUnderlying && withdrawStep !== 'withdrawn') return 'disabled'
    // For eLend sources: need deposit AFTER swap completes (Step 4)
    if (isELendSource && eulerSwapStep !== 'swapped') return 'disabled'
    // For wallet/pooled sources: need deposit AFTER wallet swap + Permit2 completes
    if (isWalletOrPooledSource && walletSwapStep !== 'swapped') return 'disabled'
    // For native deposits: need wrap to complete first
    if (isNativeDeposit && !isSwapFlow && wrapHook.step !== 'wrapped') return 'disabled'
    if (!priorStepComplete) return 'disabled'
    if (!canDeposit) return 'disabled'
    // All flows use Permit2 - check permit2 is ready
    if (permit2.status !== 'ready') return 'disabled'
    if (!finalRequest) {
      return isSimulating ? 'loading' : 'disabled'
    }
    return 'ready'
  }, [
    permit2.status,
    finalRequest,
    canDeposit,
    isSimulating,
    isWriting,
    isWaiting,
    priorStepComplete,
    isNativeDeposit,
    isSwapFlow,
    isELendSource,
    isELendSameUnderlying,
    isWalletOrPooledSource,
    eulerSwapStep,
    walletSwapStep,
    withdrawStep,
    wrapHook.step,
  ])

  // Mark as success when confirmed
  // Gateway flows disabled - only use isConfirmed
  useEffect(() => {
    if (isConfirmed && !isSuccess) {
      setIsSuccess(true)
    }
  }, [isConfirmed, isSuccess])

  const invalidateSubaccounts = useSubaccountStore((state) => state.invalidate)

  // Invalidate queries after successful deposit transaction
  useEffect(() => {
    if (isConfirmed && address && position) {
      invalidateBalancesCache({
        queryClient,
        chainId,
        address,
        position,
        vaultAddress: evault.address as Address,
      })
      if (isELendDestination) {
        invalidateSubaccounts()
      }
    }
  }, [isConfirmed, address, position, chainId, queryClient, evault.address, isELendDestination, invalidateSubaccounts])

  const displayError = useMemo(() => {
    // Timeout error takes priority for better UX feedback
    if (depositTimeout.isTimedOut) {
      return depositTimeout.displayError
    }
    const errors = [
      permit2.error,
      simulationError,
      writeError,
      waitError,
      wrapHook.error,
      routerApproval.error,
      walletSwapExecution.error,
      walletSwapQuote.error,
      eulerSwapSimulationError,
      eulerSwapWriteError,
      eulerSwapConfirmError,
      // eLend same underlying flow errors
      withdrawSimulationError,
      withdrawWriteError,
      withdrawConfirmError,
      gatewayDepositSimulationError,
      gatewayDepositWriteError,
      gatewayDepositConfirmError,
    ].filter(Boolean)
    // Filter out user rejections and non-blocking validation errors
    return errors.find((err) => !isUserRejection(err as Error) && !isNonBlockingValidationError(err as Error)) || null
  }, [
    permit2.error,
    simulationError,
    writeError,
    waitError,
    wrapHook.error,
    routerApproval.error,
    walletSwapExecution.error,
    walletSwapQuote.error,
    eulerSwapSimulationError,
    eulerSwapWriteError,
    eulerSwapConfirmError,
    // eLend same underlying flow errors
    withdrawSimulationError,
    withdrawWriteError,
    withdrawConfirmError,
    gatewayDepositSimulationError,
    gatewayDepositWriteError,
    gatewayDepositConfirmError,
    depositTimeout.isTimedOut,
    depositTimeout.displayError,
  ])

  // Get detailed error info for wallet swap errors
  const walletSwapErrorDetails = useMemo(() => {
    // Check for wallet swap execution error
    if (walletSwapExecution.error) {
      return getSwapErrorDetails(walletSwapExecution.error)
    }
    // Check for quote error
    if (walletSwapQuote.error) {
      return getSwapErrorDetails(walletSwapQuote.error)
    }
    return null
  }, [walletSwapExecution.error, walletSwapQuote.error])

  // Success state
  // Gateway swap flows disabled - only use depositToken
  if (isSuccess) {
    const successToken = isPooledSource ? depositToken.clone({ logoURI: displayToken.logoURI }) : depositToken
    return (
      <DepositSuccessView
        token={successToken}
        amount={NormalizedUnitNumber(effectiveDepositAmount)}
        onProceed={closeDialog}
        proceedText="View vault details"
      />
    )
  }

  return (
    <MultiPanelDialog>
      <DialogTitle>Deposit to {evault.name}</DialogTitle>

      <DialogPanel>
        {/* Deposit Summary */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between rounded-xl border border-primary/20 bg-primary/5 p-4">
            <div className="flex items-center gap-3">
              <TokenIcon
                token={{
                  symbol: TokenSymbol(displayToken.symbol),
                  logoURI: displayToken.logoURI,
                }}
                className="h-8 w-8"
              />
              <div className="flex flex-col">
                <span className="text-muted-foreground text-xs">{isSwapFlow ? 'Swapping' : 'Deposit Amount'}</span>
                <div className="flex items-center gap-1.5">
                  <span className="font-medium text-sm text-white">{displayToken.symbol}</span>
                  {(isELendSource || isELendSameUnderlying || isPooledSource) && (
                    <SourceTypeBadge type={isPooledSource ? 'pooled' : 'mewler'} />
                  )}
                </div>
              </div>
            </div>
            {isSwapFlow ? (
              <span className="font-semibold text-lg text-primary">
                {Number(displayAmount).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 6,
                })}
              </span>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={amountInput}
                  disabled={isWriting || isWaiting}
                  onChange={(e) => {
                    const normalized = normalizeDecimalInput(e.target.value)
                    if (normalized !== null) setAmountInput(normalized)
                  }}
                  className="w-32 bg-transparent text-right font-semibold text-lg text-primary outline-none placeholder:text-primary/40 disabled:opacity-50"
                />
                {maxSpendableBalanceRaw !== undefined && (
                  <button
                    type="button"
                    onClick={() => setAmountInput(formatUnits(maxSpendableBalanceRaw, depositToken.decimals))}
                    className="rounded-md bg-primary/10 px-2 py-0.5 font-medium text-primary text-xs hover:bg-primary/20"
                  >
                    MAX
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Gateway swap flows disabled - swap details removed */}

          {/* Destination sub-account selector (eLend destinations only) */}
          {isELendDestination && (
            <div className="overflow-hidden rounded-xl border border-primary/10 bg-primary/[0.02]">
              <Dialog open={selectorOpen} onOpenChange={setSelectorOpen}>
                <button
                  type="button"
                  className="flex w-full cursor-pointer items-center justify-between rounded-xl p-4 transition-colors hover:bg-primary/[0.04]"
                  onClick={() => setSelectorOpen(true)}
                >
                  <div className="flex items-center gap-3">
                    {selectedDestination ? (
                      <>
                        <div className="flex h-8 w-8 items-center justify-center rounded-full border border-primary/30 bg-primary/10">
                          {selectedDestination.isNew ? (
                            <Plus className="h-4 w-4 text-primary" />
                          ) : (
                            <span className="font-bold text-primary text-xs">#{selectedDestination.id}</span>
                          )}
                        </div>
                        <div className="flex flex-col items-start gap-0.5">
                          <span className="text-muted-foreground text-xs">Deposit to</span>
                          <span className="font-medium text-sm text-white">{selectedDestination.label}</span>
                          {selectedDestination.isNew ? (
                            <span className="text-primary/70 text-xs">New sub-account</span>
                          ) : selectedDestination.debtSymbols.length > 0 ? (
                            <div className="flex items-center gap-1.5">
                              <span className="text-muted-foreground text-xs">Debt:</span>
                              {selectedDestination.debtSymbols.map((symbol) => (
                                <div key={symbol} className="flex items-center gap-1">
                                  <TokenIcon token={{ symbol: TokenSymbol(symbol) }} className="h-3.5 w-3.5" />
                                  <span className="text-muted-foreground text-xs">{symbol}</span>
                                </div>
                              ))}
                              {selectedDestination.marketName && (
                                <span className="text-muted-foreground text-xs">{selectedDestination.marketName}</span>
                              )}
                              {selectedDestination.healthFactor !== null && (
                                <span
                                  className={cn(
                                    'text-xs',
                                    selectedDestination.healthFactor >= 3
                                      ? 'text-teal-400'
                                      : selectedDestination.healthFactor >= 2
                                        ? 'text-amber-400'
                                        : 'text-red-400',
                                  )}
                                >
                                  HF{' '}
                                  {selectedDestination.healthFactor >= 4
                                    ? '4+'
                                    : selectedDestination.healthFactor.toFixed(2)}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-xs">No debt</span>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/5">
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="flex flex-col items-start">
                          <span className="text-muted-foreground text-xs">Deposit to</span>
                          <span className="text-muted-foreground text-sm">Select position</span>
                        </div>
                      </>
                    )}
                  </div>
                  <ChevronDown className="h-4 w-4 shrink-0 text-white/50" />
                </button>

                <DialogContent
                  className="md:data-[state=closed]:slide-out-to-top-[16px] md:data-[state=open]:slide-in-from-top-[16px] border border-border bg-background px-6 pt-6 md:top-24 md:min-w-0 md:max-w-md md:pb-2"
                  aria-describedby={undefined}
                  contentVerticalPosition="top"
                  overlayClassName="z-[240]"
                  closeClassName="top-6"
                >
                  <div className="mb-4">
                    <DialogTitle className="mb-2">
                      <Typography variant="p" className="font-bold text-lg" gradient>
                        Select destination
                      </Typography>
                    </DialogTitle>
                  </div>

                  <div className="max-h-80 space-y-1 overflow-y-auto pb-4">
                    {isDestinationsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : destinations.length === 0 ? (
                      <div className="p-4 text-center">
                        <Typography variant="prompt">No positions available</Typography>
                      </div>
                    ) : (
                      destinations.map((dest) => (
                        <button
                          key={dest.isNew ? 'new' : dest.id}
                          type="button"
                          className={cn(
                            'flex w-full items-center gap-3 rounded-lg p-3 transition-colors hover:bg-muted',
                            selectedDestination?.id === dest.id && !dest.isNew && 'bg-muted',
                            selectedDestination?.isNew && dest.isNew && 'bg-muted',
                          )}
                          onClick={() => handleSelectDestination(dest)}
                        >
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5">
                            {dest.isNew ? (
                              <Plus className="h-4 w-4 text-primary" />
                            ) : (
                              <span className="font-bold text-white/70 text-xs">#{dest.id}</span>
                            )}
                          </div>
                          <div className="flex flex-1 flex-col items-start gap-0.5">
                            <span className="font-medium text-sm text-white">{dest.label}</span>
                            {dest.isNew ? (
                              <span className="text-primary/70 text-xs">Creates a new sub-account</span>
                            ) : dest.debtSymbols.length > 0 ? (
                              <div className="flex w-full items-center justify-between">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-muted-foreground text-xs">Debt:</span>
                                  {dest.debtSymbols.map((symbol) => (
                                    <div key={symbol} className="flex items-center gap-1">
                                      <TokenIcon token={{ symbol: TokenSymbol(symbol) }} className="h-4 w-4" />
                                      <span className="text-muted-foreground text-xs">{symbol}</span>
                                    </div>
                                  ))}
                                  {dest.marketName && (
                                    <span className="text-muted-foreground text-xs">{dest.marketName}</span>
                                  )}
                                  {dest.healthFactor !== null && (
                                    <span
                                      className={cn(
                                        'text-xs',
                                        dest.healthFactor >= 3
                                          ? 'text-teal-400'
                                          : dest.healthFactor >= 2
                                            ? 'text-amber-400'
                                            : 'text-red-400',
                                      )}
                                    >
                                      HF {dest.healthFactor >= 4 ? '4+' : dest.healthFactor.toFixed(2)}
                                    </span>
                                  )}
                                </div>
                                <span className="text-muted-foreground text-xs">
                                  ${dest.totalDebtUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                </span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-xs">No debt</span>
                            )}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        {/* Transaction Details */}
        <div className="mt-4 flex flex-col gap-2">
          {calculatedExpectedShares && (
            <TransactionOverviewDetailsItem label="You will receive">
              {Number(calculatedExpectedShares).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 6,
              })}{' '}
              {evault.symbol}
            </TransactionOverviewDetailsItem>
          )}
          {/* For eLend/pooled sources, show min receive amount */}
          {isELendSource ? (
            <TransactionOverviewDetailsItem label="Min. receive">
              {originalSwapQuote
                ? Number(
                    formatUnits(BigInt(originalSwapQuote.amountOutMin), originalSwapQuote.tokenOut.decimals),
                  ).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 6,
                  })
                : '0'}{' '}
              {originalSwapQuote?.tokenOut?.symbol || evault.assetSymbol}
            </TransactionOverviewDetailsItem>
          ) : isPooledSource ? (
            <TransactionOverviewDetailsItem label="Min. receive">
              {originalSwapQuote
                ? Number(
                    formatUnits(BigInt(originalSwapQuote.amountOutMin), originalSwapQuote.tokenOut.decimals),
                  ).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 6,
                  })
                : '0'}{' '}
              {originalSwapQuote?.tokenOut?.symbol || evault.assetSymbol}
            </TransactionOverviewDetailsItem>
          ) : isELendSameUnderlying ? (
            <TransactionOverviewDetailsItem label="Source Balance">
              {Number(formatUnits(sourceVaultBalance ?? 0n, depositToken.decimals)).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 6,
              })}{' '}
              {activeSymbol}
            </TransactionOverviewDetailsItem>
          ) : (
            <TransactionOverviewDetailsItem label="Wallet Balance">
              {walletBalance.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 6,
              })}{' '}
              {activeSymbol}
            </TransactionOverviewDetailsItem>
          )}
          <TransactionOverviewDetailsItem label="Supply APY">
            <span className="text-green-500">{evault.supplyAPY > 0 ? `${evault.supplyAPY.toFixed(2)}%` : '0.00%'}</span>
          </TransactionOverviewDetailsItem>
          {isELendDestination && (
            <SupplyCapDetail
              vaultAddress={evault.address}
              decimals={evault.decimals}
              symbol={evault.assetSymbol || evault.symbol}
            />
          )}

          {isWalletOrPooledSource && (
            <div className="mt-2 rounded-lg bg-blue-500/10 p-3 text-blue-400 text-sm">
              {walletSwapQuote.isLoading
                ? 'Loading swap quote...'
                : walletSwapQuote.quote
                  ? 'Tokens will be swapped to your wallet, then deposited via Permit2.'
                  : 'Unable to fetch swap quote. Please try again.'}
            </div>
          )}
          {isELendSource && isEulerSwapRouteAvailable && (
            <div className="mt-2 rounded-lg bg-purple-500/10 p-3 text-purple-400 text-sm">
              Tokens will be withdrawn from Lend vault, swapped to {evault.assetSymbol}, then deposited using Permit2.
            </div>
          )}
          {isELendSource && !isEulerSwapRouteAvailable && (
            <div className="mt-2 rounded-lg bg-red-500/10 p-3 text-red-400 text-sm">
              <strong>Swap not available for this pair.</strong> The Euler swap router could not find a route from{' '}
              {originalSwapQuote?.tokenIn?.symbol || 'source token'} to{' '}
              {originalSwapQuote?.tokenOut?.symbol || 'target token'}. Try a different deposit asset.
            </div>
          )}
        </div>

        {/* Warnings - hide for eLend/pooled sources since we're withdrawing from vault/protocol, not wallet */}
        {insufficientBalance && amountBigInt > 0n && !isELendSource && !isPooledSource && !isELendSameUnderlying && (
          <div className="mt-4 rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-3">
            <p className="text-sm text-yellow-400">
              Insufficient balance. You need{' '}
              {Number(effectiveDepositAmount).toLocaleString(undefined, { maximumFractionDigits: 6 })} {activeSymbol}{' '}
              but only have {walletBalance.toLocaleString(undefined, { maximumFractionDigits: 6 })} {activeSymbol}.
            </p>
          </div>
        )}

        {/* Loading status for wallet swap quote */}
        {isWalletOrPooledSource && walletSwapQuote.isLoading && (
          <div className="mt-4 flex items-center gap-2 text-muted-foreground text-sm">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            Getting swap quote...
          </div>
        )}

        {/* Wallet swap error display with detailed info */}
        {isWalletOrPooledSource && walletSwapErrorDetails && (
          <div className="mt-4 rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-red-400 text-sm">
            <p className="font-medium">{walletSwapErrorDetails.title}</p>
            <ul className="mt-2 list-inside list-disc text-xs opacity-90">
              {walletSwapErrorDetails.reasons.map((reason, i) => (
                <li key={i}>{reason}</li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => {
                walletSwapExecution.reset()
                walletSwapQuote.refetch()
              }}
              className="mt-3 text-blue-400 text-xs underline hover:text-blue-300"
            >
              Refresh quote and retry
            </button>
          </div>
        )}

        {/* Timeout error display - shows when operation takes too long */}
        <TimeoutErrorDisplay
          isTimedOut={depositTimeout.isTimedOut}
          onRetry={handleExecuteWithTimeout}
          isRetryDisabled={isWriting || isWaiting || ((!isNativeDeposit || isSwapFlow) && !finalRequest)}
        />

        {/* General error display (for non-swap errors or when no detailed info available) */}
        {displayError && !walletSwapErrorDetails && !isTimeoutError(displayError) && (
          <TransactionErrorPanel
            error={displayError}
            showSimulationWarning={!!simulationError && !isUserRejection(simulationError)}
          />
        )}

        {/* Specific error for eLend swap failure (simulation or execution) */}
        {isELendSource && (eulerSwapSimulationError || eulerSwapWriteError) && !displayError && (
          <div className="mt-4 rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-red-400 text-sm">
            <p className="font-medium">{eulerSwapSimulationError ? 'Swap simulation failed' : 'Swap failed'}</p>
            <p className="mt-1 text-xs opacity-90">The Lend swap could not be executed. This may happen if:</p>
            <ul className="mt-1 list-inside list-disc text-xs opacity-75">
              <li>Your Lend position is in a different sub-account</li>
              <li>There is insufficient liquidity for this swap route</li>
              <li>The transaction was rejected</li>
            </ul>
            <p className="mt-2 text-xs opacity-75">
              Try depositing from your wallet balance instead, or withdraw from Lend first and then deposit.
            </p>
          </div>
        )}

        {/* Specific error for eLend same underlying flow (withdraw or gateway deposit failure) */}
        {isELendSameUnderlying &&
          (withdrawSimulationError ||
            withdrawWriteError ||
            withdrawConfirmError ||
            gatewayDepositSimulationError ||
            gatewayDepositWriteError ||
            gatewayDepositConfirmError) && (
            <div className="mt-4 rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-red-400 text-sm">
              <p className="font-medium">
                {withdrawSimulationError || withdrawWriteError || withdrawConfirmError
                  ? 'Withdraw failed'
                  : 'Deposit failed'}
              </p>
              <p className="mt-1 text-xs opacity-90">
                {withdrawSimulationError || withdrawWriteError || withdrawConfirmError
                  ? 'Could not withdraw from Lend vault. This may happen if:'
                  : 'Could not deposit to Earn vault. This may happen if:'}
              </p>
              <ul className="mt-1 list-inside list-disc text-xs opacity-75">
                {(withdrawSimulationError || withdrawWriteError || withdrawConfirmError) && (
                  <>
                    <li>Your Lend position is in a different sub-account</li>
                    <li>Insufficient balance in the source vault</li>
                  </>
                )}
                {(gatewayDepositSimulationError || gatewayDepositWriteError || gatewayDepositConfirmError) && (
                  <>
                    <li>Token approval was not completed</li>
                    <li>Insufficient token balance after withdraw</li>
                  </>
                )}
                <li>The transaction was rejected</li>
              </ul>
              <p className="mt-2 text-xs opacity-75">Try depositing from your wallet balance instead.</p>
            </div>
          )}

        <BigBlocksBanner className="mt-4" />

        {/* Actions Grid */}
        <div className="mt-6 grid grid-cols-[auto_auto_1fr_auto] gap-x-4">
          {/* eLEND SAME UNDERLYING: Simple withdraw step (no swap) */}
          {isELendSameUnderlying && (
            <ActionRow index={1}>
              <ActionRow.Icon path={assets.actions.exchange} actionStatus={withdrawStatus} />
              <ActionRow.Title icon={<TokenIcon token={depositToken} className="h-6" />} actionStatus={withdrawStatus}>
                Withdraw from Lend
              </ActionRow.Title>
              <ActionRow.Description successMessage="Withdrawn!" actionStatus={withdrawStatus} variant="compact" />
              {}
              <ActionRow.Action onAction={handleWithdraw} status={withdrawStatus}>
                {isWithdrawPending || isWithdrawConfirming ? 'Withdrawing...' : 'Withdraw'}
              </ActionRow.Action>
            </ActionRow>
          )}

          {/* eLEND SOURCE: Step 1 - Swap from eLend to wallet */}
          {isELendSource && (
            <ActionRow index={1}>
              <ActionRow.Icon path={assets.actions.exchange} actionStatus={eulerSwapStatus} />
              <ActionRow.Title icon={<TokenIcon token={depositToken} className="h-6" />} actionStatus={eulerSwapStatus}>
                Swap from Lend
              </ActionRow.Title>
              <ActionRow.Description
                successMessage="Swapped to wallet!"
                actionStatus={eulerSwapStatus}
                variant="compact"
              />
              {}
              <ActionRow.Action onAction={handleEulerSwap} status={eulerSwapStatus}>
                {isEulerSwapPending || isEulerSwapConfirming ? 'Swapping...' : 'Swap'}
              </ActionRow.Action>
            </ActionRow>
          )}

          {/* WALLET/POOLED SWAP FLOW: Approve Router → Swap → Approve Vault → Deposit */}
          {isWalletOrPooledSource && (
            <>
              {/* Step 1: Approve router for tokenIn (ERC20 only) */}
              {/* Native HYPE swaps don't need approval - they use msg.value */}
              {showSwapRouterApprovalStep && (
                <ActionRow index={1}>
                  <ActionRow.Icon path={assets.actions.approve} actionStatus={swapApproveStatus} />
                  <ActionRow.Title
                    icon={<TokenIcon token={depositToken} className="h-6" />}
                    actionStatus={swapApproveStatus}
                  >
                    Approve {displayToken.symbol}
                  </ActionRow.Title>
                  <ActionRow.Description
                    successMessage="Approved!"
                    actionStatus={swapApproveStatus}
                    variant="compact"
                  />
                  <ActionRow.Action onAction={routerApproval.approve} status={swapApproveStatus}>
                    {routerApproval.isLoading ? 'Approving...' : 'Approve'}
                  </ActionRow.Action>
                </ActionRow>
              )}

              {/* Step 2 (or 1 when no router approval step): Execute wallet swap */}
              <ActionRow index={1 + walletSwapStepOffset}>
                <ActionRow.Icon path={assets.actions.exchange} actionStatus={walletSwapStatus} />
                <ActionRow.Title
                  icon={<TokenIcon token={depositToken} className="h-6" />}
                  actionStatus={walletSwapStatus}
                >
                  Swap {displayToken.symbol}
                </ActionRow.Title>
                <ActionRow.Description successMessage="Swapped!" actionStatus={walletSwapStatus} variant="compact" />
                <ActionRow.Action onAction={handleWalletSwap} status={walletSwapStatus}>
                  {walletSwapQuote.isLoading
                    ? 'Getting quote...'
                    : walletSwapExecution.isLoading || walletSwapStep === 'swapping'
                      ? 'Swapping...'
                      : walletSwapExecution.error
                        ? 'Retry'
                        : 'Swap'}
                </ActionRow.Action>
              </ActionRow>

              {/* Step 3 (or 2 when no router approval step): Approve Permit2 for swapped token */}
              <ActionRow index={2 + walletSwapStepOffset}>
                <ActionRow.Icon path={assets.actions.approve} actionStatus={approveStatus} />
                <ActionRow.Title
                  icon={<TokenIcon token={{ symbol: evault.assetSymbol || 'TOKEN' } as any} className="h-6" />}
                  actionStatus={approveStatus}
                >
                  Approve Permit2
                </ActionRow.Title>
                <ActionRow.Description successMessage="Approved!" actionStatus={approveStatus} variant="compact" />
                <ActionRow.Action onAction={() => permit2.approvePermit2?.()} status={approveStatus}>
                  Approve
                </ActionRow.Action>
              </ActionRow>

              {/* Step 4 (or 3 when no router approval step): Sign Permit2 */}
              <ActionRow index={3 + walletSwapStepOffset}>
                <ActionRow.Icon path={assets.actions.approve} actionStatus={signStatus} />
                <ActionRow.Title
                  icon={<TokenIcon token={{ symbol: evault.assetSymbol || 'TOKEN' } as any} className="h-6" />}
                  actionStatus={signStatus}
                >
                  Sign Permit2
                </ActionRow.Title>
                <ActionRow.Description successMessage="Signed!" actionStatus={signStatus} variant="compact" />
                <ActionRow.Action onAction={() => permit2.signPermit2?.()} status={signStatus}>
                  Sign
                </ActionRow.Action>
              </ActionRow>

              {/* Step 5 (or 4 when no router approval step): Deposit */}
              <ActionRow index={4 + walletSwapStepOffset}>
                <ActionRow.Icon path={assets.actions.deposit} actionStatus={executeStatus} />
                <ActionRow.Title
                  icon={<TokenIcon token={{ symbol: evault.assetSymbol || 'TOKEN' } as any} className="h-6" />}
                  actionStatus={executeStatus}
                >
                  Deposit
                </ActionRow.Title>
                <ActionRow.Description successMessage="Deposited!" actionStatus={executeStatus} variant="compact" />
                <ActionRow.Action onAction={handleExecuteWithTimeout} status={executeStatus}>
                  {depositTimeout.isTimedOut ? 'Retry' : 'Deposit'}
                </ActionRow.Action>
              </ActionRow>
            </>
          )}

          {/* eLend same underlying: Withdraw → Permit2 Approve → Permit2 Sign → Deposit */}
          {isELendSameUnderlying && (
            <>
              {/* Step 2: Approve Permit2 for withdrawn token */}
              <ActionRow index={2}>
                <ActionRow.Icon path={assets.actions.approve} actionStatus={approveStatus} />
                <ActionRow.Title icon={<TokenIcon token={depositToken} className="h-6" />} actionStatus={approveStatus}>
                  Approve Permit2
                </ActionRow.Title>
                <ActionRow.Description successMessage="Approved!" actionStatus={approveStatus} variant="compact" />
                <ActionRow.Action onAction={() => permit2.approvePermit2?.()} status={approveStatus}>
                  Approve
                </ActionRow.Action>
              </ActionRow>

              {/* Step 3: Sign Permit2 */}
              <ActionRow index={3}>
                <ActionRow.Icon path={assets.actions.approve} actionStatus={signStatus} />
                <ActionRow.Title icon={<TokenIcon token={depositToken} className="h-6" />} actionStatus={signStatus}>
                  Sign Permit2
                </ActionRow.Title>
                <ActionRow.Description successMessage="Signed!" actionStatus={signStatus} variant="compact" />
                <ActionRow.Action onAction={() => permit2.signPermit2?.()} status={signStatus}>
                  Sign
                </ActionRow.Action>
              </ActionRow>

              {/* Step 4: Deposit */}
              <ActionRow index={4}>
                <ActionRow.Icon path={assets.actions.deposit} actionStatus={executeStatus} />
                <ActionRow.Title icon={<TokenIcon token={depositToken} className="h-6" />} actionStatus={executeStatus}>
                  Deposit
                </ActionRow.Title>
                <ActionRow.Description successMessage="Deposited!" actionStatus={executeStatus} variant="compact" />
                <ActionRow.Action onAction={handleExecuteWithTimeout} status={executeStatus}>
                  {depositTimeout.isTimedOut ? 'Retry' : 'Deposit'}
                </ActionRow.Action>
              </ActionRow>
            </>
          )}

          {/* Deposit steps: Approve Permit2, Sign Permit2, Deposit */}
          {/* Skip for native HYPE - has its own section above */}
          {/* Skip for wallet/pooled swap flows - has its own section above */}
          {/* Skip for eLend same underlying - has its own section above */}
          {/* Show Permit2 steps for: eLend sources (after swap), direct deposits */}
          {!(isNativeDeposit && !isSwapFlow) &&
            !(isSwapFlow && !isELendSource && !isELendSameUnderlying) &&
            !isELendSameUnderlying && (
              <>
                <ActionRow
                  index={
                    isELendSource
                      ? 2 // eLend source with swap: step 1 is Swap, step 2 is Approve Permit2
                      : 1 // Direct deposit: step 1 is Approve Permit2
                  }
                >
                  <ActionRow.Icon path={assets.actions.approve} actionStatus={approveStatus} />
                  <ActionRow.Title
                    icon={<TokenIcon token={depositToken} className="h-6" />}
                    actionStatus={approveStatus}
                  >
                    Approve Permit2
                  </ActionRow.Title>
                  <ActionRow.Description successMessage="Approved!" actionStatus={approveStatus} variant="compact" />
                  <ActionRow.Action onAction={() => permit2.approvePermit2?.()} status={approveStatus}>
                    Approve
                  </ActionRow.Action>
                </ActionRow>

                <ActionRow
                  index={
                    isELendSource
                      ? 3 // eLend source with swap: step 1 is Swap, step 2 is Approve, step 3 is Sign
                      : 2 // Direct deposit: step 1 is Approve, step 2 is Sign
                  }
                >
                  <ActionRow.Icon path={assets.actions.approve} actionStatus={signStatus} />
                  <ActionRow.Title icon={<TokenIcon token={depositToken} className="h-6" />} actionStatus={signStatus}>
                    Sign Permit2
                  </ActionRow.Title>
                  <ActionRow.Description successMessage="Signed!" actionStatus={signStatus} variant="compact" />
                  <ActionRow.Action onAction={() => permit2.signPermit2?.()} status={signStatus}>
                    Sign
                  </ActionRow.Action>
                </ActionRow>

                {/* Final Deposit step for Permit2 flows */}
                <ActionRow
                  index={
                    isELendSource
                      ? 4 // eLend source with swap: step 1 is Swap, step 2 is Approve, step 3 is Sign, step 4 is Deposit
                      : 3 // Direct deposit: step 1 is Approve, step 2 is Sign, step 3 is Deposit
                  }
                >
                  <ActionRow.Icon path={assets.actions.deposit} actionStatus={executeStatus} />
                  <ActionRow.Title
                    icon={<TokenIcon token={depositToken} className="h-6" />}
                    actionStatus={executeStatus}
                  >
                    Deposit
                  </ActionRow.Title>
                  <ActionRow.Description successMessage="Deposited!" actionStatus={executeStatus} variant="compact" />
                  <ActionRow.Action onAction={handleExecuteWithTimeout} status={executeStatus}>
                    {depositTimeout.isTimedOut ? 'Retry' : 'Deposit'}
                  </ActionRow.Action>
                </ActionRow>
              </>
            )}

          {/* Native HYPE deposit flow: Wrap → Permit2 Approve → Permit2 Sign → Deposit */}
          {isNativeDeposit && !isSwapFlow && (
            <>
              {/* Step 1: Wrap HYPE → WHYPE */}
              <ActionRow index={1}>
                <ActionRow.Icon path={assets.actions.exchange} actionStatus={wrapStatus} />
                <ActionRow.Title icon={<TokenIcon token={depositToken} className="h-6" />} actionStatus={wrapStatus}>
                  Wrap HYPE
                </ActionRow.Title>
                <ActionRow.Description successMessage="Wrapped!" actionStatus={wrapStatus} variant="compact" />
                <ActionRow.Action onAction={wrapHook.wrap} status={wrapStatus}>
                  {wrapHook.isLoading ? 'Wrapping...' : wrapHook.error ? 'Retry' : 'Wrap'}
                </ActionRow.Action>
              </ActionRow>

              {/* Step 2: Approve Permit2 for WHYPE */}
              <ActionRow index={2}>
                <ActionRow.Icon path={assets.actions.approve} actionStatus={approveStatus} />
                <ActionRow.Title icon={<TokenIcon token={depositToken} className="h-6" />} actionStatus={approveStatus}>
                  Approve Permit2
                </ActionRow.Title>
                <ActionRow.Description successMessage="Approved!" actionStatus={approveStatus} variant="compact" />
                <ActionRow.Action onAction={() => permit2.approvePermit2?.()} status={approveStatus}>
                  Approve
                </ActionRow.Action>
              </ActionRow>

              {/* Step 3: Sign Permit2 */}
              <ActionRow index={3}>
                <ActionRow.Icon path={assets.actions.approve} actionStatus={signStatus} />
                <ActionRow.Title icon={<TokenIcon token={depositToken} className="h-6" />} actionStatus={signStatus}>
                  Sign Permit2
                </ActionRow.Title>
                <ActionRow.Description successMessage="Signed!" actionStatus={signStatus} variant="compact" />
                <ActionRow.Action onAction={() => permit2.signPermit2?.()} status={signStatus}>
                  Sign
                </ActionRow.Action>
              </ActionRow>

              {/* Step 4: Deposit */}
              <ActionRow index={4}>
                <ActionRow.Icon path={assets.actions.deposit} actionStatus={executeStatus} />
                <ActionRow.Title icon={<TokenIcon token={depositToken} className="h-6" />} actionStatus={executeStatus}>
                  Deposit
                </ActionRow.Title>
                <ActionRow.Description successMessage="Deposited!" actionStatus={executeStatus} variant="compact" />
                <ActionRow.Action onAction={handleExecuteWithTimeout} status={executeStatus}>
                  {depositTimeout.isTimedOut ? 'Retry' : 'Deposit'}
                </ActionRow.Action>
              </ActionRow>
            </>
          )}
        </div>
      </DialogPanel>
    </MultiPanelDialog>
  )
}
